<template>
  <div v-bind="$attrs" class="rounded-md border border-line p-12 shadow-md">
    <div class="mb-8 flex items-center justify-between">
      <div class="flex items-center gap-8">
        <span
          class="flex h-32 w-32 items-center justify-center rounded-full border border-line"
        >
          <JIcon name="location" size="14" class="text-gray-500" />
        </span>

        <span class="text-subject font-medium text-dark">
          {{ car.cityName }}, {{ car.countryName }}
        </span>
      </div>

      <JButton
        icon="trash-bin"
        color="danger"
        variant="tonal"
        size="xs"
        class="rounded-full"
        @click.stop="setIsOpen(true)"
      />
    </div>

    <div class="mb-8 flex items-center gap-12">
      <JImage
        :src="car.imageURL"
        :alt="car.name"
        containerClass="w-[80px] aspect-square rounded-2xl"
        class="object-contain"
      />

      <div class="flex flex-col">
        <strong class="text-pane-header text-dark">{{ car.name }}</strong>
        <div>
          <span class="text-subject font-medium text-gray-600">
            {{ car.provider }}
          </span>
          <span class="mx-4 text-subject font-medium text-gray-300">|</span>
          <div
            class="inline-flex cursor-pointer text-subject font-medium text-primary underline"
            @click="showDetails"
          >
            <span> {{ $t('search.carDetails') }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="flex items-center gap-8 text-gray-600">
      <span class="rounded-sm bg-gray-200 px-4">
        {{ $t('pickUp') }}
        <time class="font-medium"
          >{{ pickUpDate }} {{ car.detail.pickUpDetails.address3 }}
          {{ $t('airport') }}</time
        >
      </span>
      <span class="rounded-sm bg-gray-200 px-4">
        {{ $t('dropOff') }}
        <time class="font-medium"
          >{{ dropOffDate }} {{ car.detail.dropOffDetails.address3 }}
          {{ $t('airport') }}</time
        >
      </span>
    </div>
  </div>

  <JConfirmation
    :modelValue="isOpen"
    :title="$t('search.areYouSureYouWantToRemoveThisItem')"
    color="danger"
    :confirmText="$t('words.remove')"
    :cancelText="$t('words.cancel')"
    @update:model-value="setIsOpen"
    @confirm="onDelete"
    @cancel="setIsOpen(false)"
  />
</template>

<script setup lang="ts">
import { useToggle } from '@vueuse/core'
import { JButton, JConfirmation, JIcon, JImage } from '@journey/ui-kit'
import { inject } from 'vue'
import type { Props } from './ItinerarySummaryCarCardContent.types'
import { useFormatDate } from '@/modules/Core/composables'
import useItinerarySummary from '@/modules/Search/store/itinerarySummary'
import { useItinerary } from '@/modules/Search/composables'
import { showCarDetailsInjectionKey } from '@/modules/Search/constants'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps<Props>()
const showCarDetail = inject(showCarDetailsInjectionKey)

function showDetails() {
  if (!showCarDetail) {
    return
  }

  showCarDetail.showCarDetail(props.car)
}

const { formattedDate: pickUpDate } = useFormatDate(
  props.car.pickupDateTime,
  'dd MMM (HH:mm)'
)
const { formattedDate: dropOffDate } = useFormatDate(
  props.car.dropoffDateTime,
  'dd MMM (HH:mm)'
)

const itinerarySummaryStore = useItinerarySummary()

const { submitItinerary } = useItinerary()

const [isOpen, setIsOpen] = useToggle(false)

async function onDelete() {
  itinerarySummaryStore.remove(props.car.uniqueId)

  const { data } = await submitItinerary()

  itinerarySummaryStore.setItineraries(data.data.itinerary)

  setIsOpen(false)
}
</script>
